import { Exclude, Transform, Type } from 'class-transformer';
import {
  IsArray,
  IsDefined,
  IsEnum,
  IsIn,
  IsNumber,
  IsOptional,
  IsString,
  ValidateNested,
} from 'class-validator';
import { EcomCouponSettings, EcomVoucher } from './coupon';
import { EcomProductType } from './base-product';
import { MediaFile } from '../media';

export class EcomCampaign {
  _id?: string;

  @IsDefined()
  @IsString()
  @Transform((p) => p?.toString(), { toClassOnly: true })
  campaignID!: string;

  @IsDefined()
  @IsString()
  campaignTitle!: string;

  @IsDefined()
  @IsString()
  campaignAvailableFrom!: string | Date;

  @IsDefined()
  @IsString()
  campaignAvailableTo!: string | Date;

  @IsOptional()
  @IsString()
  campaignImage?: string;

  @IsOptional()
  @IsString()
  campaignEligibility?: string;

  @IsOptional()
  @IsString()
  campaignTerms?: string;

  @IsOptional()
  @IsString()
  campaignType!: 'ecom' | 'crm' | 'both';

  @IsOptional()
  @IsString()
  campaignVinType!: 'stock' | 'previn' | 'both';

  @ValidateNested({ each: true })
  @IsArray()
  @Type(() => EcomCampaignAppliedItem)
  appliedItems?: EcomCampaignAppliedItem[];

  @ValidateNested({ each: true })
  @IsArray()
  @Type(() => EcomVoucher)
  cashVoucher?: EcomVoucher[];

  @ValidateNested()
  @Type(() => EcomCouponSettings)
  couponCashBack?: EcomCouponSettings;

  @ValidateNested({ each: true })
  @Type(() => EcomCampaignFinanceOffer)
  financeDetails?: EcomCampaignFinanceOffer[];

  @IsOptional()
  @IsArray()
  @IsNumber({}, { each: true })
  exception?: number[];

  @Transform((value) => value?.toString()?.split(','))
  connectedCampaignID?: string[];

  connectedCampaignTitle?: string;
}

// tslint:disable-next-line: max-classes-per-file
export class ExcludedDiscount {
  @IsDefined()
  @IsIn(['cash', 'pool', 'tradeIn'])
  discountMode!: 'cash' | 'pool' | 'tradeIn';

  @IsDefined()
  id!: string;
}

// tslint:disable-next-line: max-classes-per-file
export class EcomCampaignFinanceOffer {
  @IsDefined()
  @IsNumber()
  bankID!: number;

  @IsDefined()
  @IsString()
  campDiscountTitle!: string;

  @IsOptional()
  @IsNumber()
  campDiscountPercentage?: number;

  @IsOptional()
  @IsNumber()
  campCashback?: number;

  @IsOptional()
  @IsNumber()
  numOfFreeEmi?: number;

  @Exclude()
  campaignID!: string;

  @IsOptional()
  @Transform((p) => p === 'Yes')
  documentsRequired?: boolean;

  @IsOptional()
  documentsRequiredType?: 'All' | 'Any';

  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => ExcludedDiscount)
  excludedDiscounts?: ExcludedDiscount[];

  @IsOptional()
  @Transform((value) => value?.toString()?.split(','))
  mandatoryDocuments?: string[];

  @IsOptional()
  @Transform((value) => value?.toString()?.split(','))
  optionalDocuments?: string[];

  @IsOptional()
  internalRate?: number;

  @IsOptional()
  downPayment?: number;

  @IsOptional()
  @Transform((p) => p === 'Yes')
  tenureDiscount?: boolean;

  @IsOptional()
  tenureInMonths?: number;

  @Exclude()
  documents?: CampaignDiscountDocument[];

  @IsOptional()
  @Transform((p) => p === 'Yes')
  financeCalculationAtVehiclePrice?: boolean;
}

// tslint:disable-next-line: max-classes-per-file
export class EcomCampaignAppliedItem {
  @IsDefined()
  @IsString()
  @Transform((p) => p?.toString(), { toClassOnly: true })
  productID!: string;

  @IsDefined()
  @IsString()
  @Transform((p) => p?.toString(), { toClassOnly: true })
  productOptionID!: string;

  @IsDefined()
  @IsString()
  campDiscountTitle!: string;

  @IsDefined()
  @IsString()
  @IsEnum(EcomProductType)
  productType!: EcomProductType;

  @IsDefined()
  @IsString()
  campDiscountType!: 'percentage' | 'fixed';

  @IsDefined()
  @IsNumber()
  campDiscountValue!: number;

  @IsOptional()
  @IsNumber()
  campCashback?: number;

  @IsOptional()
  @IsNumber()
  campInternalPrice?: number;

  @IsOptional()
  @IsNumber()
  sortOrder?: number;

  @IsOptional()
  @Transform((p) => p === 'Yes')
  campaignDiscountApplyCart?: boolean;

  @IsOptional()
  campaignDiscountApplyCartText?: string;

  @IsOptional()
  campInternalPriceType!: 'fixed' | 'percentage';

  @IsOptional()
  @Transform((p) => p === 'Yes')
  documentsRequired?: boolean;

  @IsOptional()
  documentsRequiredType!: 'All' | 'Any';

  @IsOptional()
  @Transform((value) => value?.toString()?.split(','))
  mandatoryDocuments?: string[];

  @IsOptional()
  @Transform((value) => value?.toString()?.split(','))
  optionalDocuments?: string[];

  @Exclude()
  documents?: CampaignDiscountDocument[];

  @IsOptional()
  @Transform((p) => p === 'Yes')
  financeCalculationAtVehiclePrice?: boolean;

  @IsOptional()
  upgradeOption?: boolean;
}

export interface CampaignDiscountDocument {
  name: string;
  documentTypeId: string;
  uploadedFrom: 'ecom' | 'crm';
  crmDocument?: MediaFile;
  ecomDocument?: string;
}
